import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { formatDate } from '../utils/tatCalculator';

const CourierTracking = ({ courierData, courierName }) => {
  if (!courierData) {
    return React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        mb: 4,
        backgroundColor: 'background.paper'
      }
    },
      React.createElement(Typography, {
        color: 'text.secondary'
      }, 'No courier tracking information available')
    );
  }

  // Helper function to format BlueDart pickup date: "03 Jan 2026, 14:15"
  const formatPickupDate = (pickupDate, pickupTime, courierName) => {
    if (!pickupDate) return 'N/A';
    
    const courier = (courierName || '').toLowerCase();
    const isBlueDart = courier.includes('bluedart') || courier.includes('blue dart');
    
    try {
      const dateStr = String(pickupDate).trim();
      const pickupTimeStr = pickupTime ? String(pickupTime).trim() : '';
      
      // Check if pickupDate already contains time (format: "03 January 2026 1415")
      const timePattern = /\s+(\d{4})\s+(\d{4})$/; // Matches: space + year + space + time
      const match = dateStr.match(timePattern);
      
      let dateObj;
      let combinedStr;
      
      if (match) {
        // Time is already in pickupDate: "03 January 2026 1415"
        const year = match[1];
        const timePart = match[2];
        const datePart = dateStr.replace(/\s+\d{4}\s+\d{4}$/, '').trim();
        const timeFormatted = `${timePart.substring(0, 2)}:${timePart.substring(2)}`;
        combinedStr = `${datePart} ${year} ${timeFormatted}`;
      } else if (pickupTimeStr && pickupTimeStr.length === 4 && /^\d{4}$/.test(pickupTimeStr)) {
        // Separate pickupDate and pickupTime fields (for BlueDart or any courier with 4-digit time)
        const timeFormatted = `${pickupTimeStr.substring(0, 2)}:${pickupTimeStr.substring(2)}`;
        combinedStr = `${dateStr.trim()} ${timeFormatted}`;
      } else {
        // No time available, use default formatDate
        return formatDate(pickupDate);
      }
      
      // Parse "03 January 2026 14:15" format using regex
      const months = {
        'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
        'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
      };
      
      const dateTimePattern = /^(\d{1,2})\s+(\w+)\s+(\d{4})\s+(\d{1,2}):(\d{2})$/;
      const dateTimeMatch = combinedStr.match(dateTimePattern);
      
      if (dateTimeMatch) {
        const day = parseInt(dateTimeMatch[1], 10);
        const monthName = dateTimeMatch[2].toLowerCase();
        const year = parseInt(dateTimeMatch[3], 10);
        const hours = parseInt(dateTimeMatch[4], 10);
        const minutes = parseInt(dateTimeMatch[5], 10);
        
        if (months[monthName] !== undefined && !isNaN(day) && !isNaN(year) && !isNaN(hours) && !isNaN(minutes)) {
          dateObj = new Date(year, months[monthName], day, hours, minutes);
        }
      }
      
      if (dateObj && !isNaN(dateObj.getTime())) {
        const day = dateObj.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        const hours24 = dateObj.getHours();
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        
        // Format time (12-hour format with am/pm)
        const period = hours24 >= 12 ? 'pm' : 'am';
        const displayHours = hours24 % 12 || 12;
        const formattedTime = `${displayHours.toString().padStart(2, '0')}:${minutes} ${period}`;
        
        return `${day} ${month} ${year}, ${formattedTime}`;
      }
      
      // Fallback to default formatDate
      return formatDate(pickupDate);
    } catch (e) {
      return formatDate(pickupDate);
    }
  };

  // Helper function to format BlueDart delivered date: "10 Jan 2025, 16:55"
  const formatDeliveredDate = (courierData, courierName) => {
    if (!courierData) return 'N/A';
    
    const courier = (courierName || '').toLowerCase();
    const isBlueDart = courier.includes('bluedart') || courier.includes('blue dart');
    
    try {
      // Try to get delivered date from scans array (scanType == "DL")
      let deliveryDate = null;
      let deliveryTime = null;
      
      const scans = courierData.scans || [];
      let scansArray = scans;
      
      // Handle if scans is a string (JSON)
      if (typeof scans === 'string') {
        try {
          scansArray = JSON.parse(scans);
        } catch {
          scansArray = [];
        }
      }
      
      // Partner-specific logic: Check for Movin and Delhivery Enterprise delivery partners
      const deliveryPartnerName = courierData.delivery_partner_name || courierData.deliveryPartnerName;
      const deliveryPartnerLower = (deliveryPartnerName || '').toLowerCase();
      const isMovin = deliveryPartnerLower.includes('movin');
      const isDelhiveryEnterprise = deliveryPartnerLower.includes('delhivery') && deliveryPartnerLower.includes('enterprise');
      
      // Find scan with delivered status
      for (const scan of scansArray) {
        if (scan) {
          const scanType = String(scan.scanType || '').toUpperCase();
          const scanStatus = String(scan.status || '').toUpperCase();
          const remarks = String(scan.remarks || scan.Remarks || '').toLowerCase();
          
          // Movin-specific: Look for scanType = "DELIVERED_TO_CUSTOMER"
          if (isMovin && scanType === 'DELIVERED_TO_CUSTOMER') {
            deliveryDate = scan.scanDate; // Format: "10-Nov-2025"
            deliveryTime = scan.scanTime; // Format: "16:55"
            break;
          }
          
          // Delhivery Enterprise-specific: Look for remarks containing "Shipment Delivered"
          if (isDelhiveryEnterprise && remarks && (
            remarks.includes('shipment delivered') || 
            remarks.includes('delivered') ||
            (scanType && scanType.includes('DELIVERED'))
          )) {
            deliveryDate = scan.scanDate; // Format: "10-Nov-2025"
            deliveryTime = scan.scanTime; // Format: "16:55"
            break;
          }
          
          // Generic delivered scan detection (for other partners)
          if (!isMovin && !isDelhiveryEnterprise) {
            const isDelivered = (
              scanType === 'DL' ||  // Support legacy generic value
              'DELIVERED' in scanType ||
              'DELIVERED' in scanStatus
            );
            
            if (isDelivered) {
              deliveryDate = scan.scanDate; // Format: "10-Nov-2025"
              deliveryTime = scan.scanTime; // Format: "16:55"
              break;
            }
          }
        }
      }
      
      // Fallback to deliveredTime if not found in scans
      if (!deliveryDate) {
        const deliveredTimeField = courierData.deliveredDate || courierData.delivered_date || courierData.deliveryDate || courierData.deliveredTime;
        if (deliveredTimeField) {
          return formatDate(deliveredTimeField);
        }
        return 'N/A';
      }
      
      // For BlueDart, format as "10 Jan 2025, 16:55"
      if (isBlueDart && deliveryDate && deliveryTime) {
        // Parse "10-Nov-2025" format
        const dateParts = deliveryDate.split('-');
        if (dateParts.length === 3) {
          const day = parseInt(dateParts[0], 10);
          const monthAbbr = dateParts[1].toLowerCase();
          const year = parseInt(dateParts[2], 10);
          
          const monthMap = {
            'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
            'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
          };
          
          const monthIndex = monthMap[monthAbbr];
          
          // Parse time "16:55"
          const timeParts = deliveryTime.split(':');
          const hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);
          
          if (monthIndex !== undefined && !isNaN(day) && !isNaN(year) && !isNaN(hours) && !isNaN(minutes)) {
            const dateObj = new Date(year, monthIndex, day, hours, minutes);
            if (!isNaN(dateObj.getTime())) {
              const formattedDay = dateObj.getDate().toString().padStart(2, '0');
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const month = monthNames[dateObj.getMonth()];
              const formattedYear = dateObj.getFullYear();
              const hours24 = dateObj.getHours();
              const formattedMinutes = dateObj.getMinutes().toString().padStart(2, '0');
              
              // Format time (12-hour format with am/pm)
              const period = hours24 >= 12 ? 'pm' : 'am';
              const displayHours = hours24 % 12 || 12;
              const formattedTime = `${displayHours.toString().padStart(2, '0')}:${formattedMinutes} ${period}`;
              
              return `${formattedDay} ${month} ${formattedYear}, ${formattedTime}`;
            }
          }
        }
      }
      
      // Fallback: combine date and time and use formatDate
      if (deliveryDate && deliveryTime) {
        const combined = `${deliveryDate} ${deliveryTime}`;
        return formatDate(combined);
      }
      
      return formatDate(deliveryDate || courierData.deliveredTime);
    } catch (e) {
      const deliveredTimeField = courierData.deliveredDate || courierData.delivered_date || courierData.deliveryDate || courierData.deliveredTime;
      return formatDate(deliveredTimeField);
    }
  };

  // Helper function to format expected delivery date: "06 Jan 2026, 11:59 pm" (end of day)
  const formatExpectedDelivery = (expectedDelivery, courierName) => {
    if (!expectedDelivery) return 'N/A';
    
    const courier = (courierName || '').toLowerCase();
    const isBlueDart = courier.includes('bluedart') || courier.includes('blue dart');
    const isEkart = courier.includes('ekart');
    
    try {
      const dateStr = String(expectedDelivery).trim();
      let dateObj;
      
      // Try ISO format first
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        dateObj = new Date(dateStr);
      } else {
        // Try "DD Month YYYY" format (Bluedart)
        const months = {
          'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
          'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
        };
        
        const datePattern = /^(\d{1,2})\s+(\w+)\s+(\d{4})/;
        const match = dateStr.match(datePattern);
        
        if (match) {
          const day = parseInt(match[1], 10);
          const monthName = match[2].toLowerCase();
          const year = parseInt(match[3], 10);
          
          if (months[monthName] !== undefined && !isNaN(day) && !isNaN(year)) {
            dateObj = new Date(year, months[monthName], day);
          }
        } else if (isEkart && /^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
          // Try "DD-MM-YYYY" format (Ekart edd format like "07-01-2026")
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
            const year = parseInt(parts[2], 10);
            dateObj = new Date(year, month, day);
          }
        } else {
          dateObj = new Date(dateStr);
        }
      }
      
      if (dateObj && !isNaN(dateObj.getTime())) {
        // If time is midnight (00:00:00), set to end of day (23:59:59)
        if (dateObj.getHours() === 0 && dateObj.getMinutes() === 0 && dateObj.getSeconds() === 0) {
          dateObj.setHours(23, 59, 59);
        }
        
        const day = dateObj.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        const hours24 = dateObj.getHours();
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        
        // Format time (12-hour format with am/pm)
        const period = hours24 >= 12 ? 'pm' : 'am';
        const displayHours = hours24 % 12 || 12;
        const formattedTime = `${displayHours.toString().padStart(2, '0')}:${minutes} ${period}`;
        
        return `${day} ${month} ${year}, ${formattedTime}`;
      }
      
      // Fallback to default formatDate
      return formatDate(expectedDelivery);
    } catch (e) {
      return formatDate(expectedDelivery);
    }
  };

  const InfoRow = ({ label, value }) => {
    return React.createElement(Grid, { container: true, spacing: 2, sx: { mb: 1 } },
      React.createElement(Grid, { item: true, xs: 4 },
        React.createElement(Typography, {
          variant: 'body2',
          color: 'textSecondary'
        }, label)
      ),
      React.createElement(Grid, { item: true, xs: 8 },
        React.createElement(Typography, {
          variant: 'body2',
          fontWeight: 'medium'
        }, value || 'N/A')
      )
    );
  };

  return React.createElement(Box, {
    sx: {
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 3,
      p: { xs: 3, sm: 4 },
      mb: 4,
      backgroundColor: 'background.paper'
    }
  },
    React.createElement(Box, {
      sx: {
        display: 'flex',
        alignItems: 'center',
        mb: 3
      }
    },
      React.createElement(LocalShippingIcon, {
        sx: { mr: 1.5, fontSize: '1.5rem' }
      }),
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }, 'Courier Tracking Details')
    ),
    React.createElement(Divider, { sx: { mb: 3 } }),
    React.createElement(Box, null,
      React.createElement(InfoRow, {
        label: 'AWB Number',
        value: courierData.awb || courierData.docketNo
      }),
      React.createElement(InfoRow, {
        label: 'Status',
        value: courierData.status || courierData.docketTrackingStatus
      }),
      (courierName && (courierName.toLowerCase().includes('shiprocket') || courierData.courierName && courierData.courierName.toLowerCase().includes('shiprocket')) && courierData.delivery_partner_name) && React.createElement(InfoRow, {
        label: 'Delivery Partner',
        value: courierData.delivery_partner_name
      }),
      React.createElement(InfoRow, {
        label: 'Origin',
        value: courierData.origin || courierData.consignorName
      }),
      React.createElement(InfoRow, {
        label: 'Destination',
        value: courierData.destination || courierData.consigneeName
      }),
      React.createElement(InfoRow, {
        label: 'Pickup Date',
        value: formatPickupDate(
          courierData.pickupDate,
          courierData.pickupTime,
          courierName || courierData.courierName || courierData.courier || ''
        )
      }),
      React.createElement(InfoRow, {
        label: 'Expected Delivery',
        value: formatExpectedDelivery(
          courierData.expectedDelivery || courierData.edd,
          courierName || courierData.courierName || courierData.courier || ''
        )
      }),
      React.createElement(InfoRow, {
        label: 'Delivered Date',
        value: formatDeliveredDate(
          courierData,
          courierName || courierData.courierName || courierData.courier || ''
        )
      }),
      courierData.deliveredTime && React.createElement(InfoRow, {
        label: 'Delivered Time',
        value: formatDate(courierData.deliveredTime)
      }),
      courierData.receivedBy && React.createElement(InfoRow, {
        label: 'Received By',
        value: courierData.receivedBy
      }),
      courierData.delivered !== undefined && React.createElement(Box, { sx: { mt: 2 } },
        React.createElement(Chip, {
          label: courierData.delivered ? 'Delivered' : 'In Transit',
          size: 'small',
          sx: {
            backgroundColor: courierData.delivered ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 500,
            borderRadius: 2
          }
        })
      ),
      (courierData.scans !== undefined && courierData.scans !== null) && React.createElement(Accordion, {
        sx: { mt: 2 }
      },
        React.createElement(AccordionSummary, {
          expandIcon: React.createElement(ExpandMoreIcon, null)
        },
          React.createElement(Typography, null, 'Scan History')
        ),
        React.createElement(AccordionDetails, null,
          (() => {
            // Parse scans if it's a string, otherwise use as-is
            let scansData = courierData.scans;
            if (typeof scansData === 'string') {
              try {
                scansData = JSON.parse(scansData);
              } catch (e) {
                return React.createElement(Typography, {
                  variant: 'body2',
                  color: 'error'
                }, 'Invalid scan data format');
              }
            }

            // Ensure it's an array
            if (!Array.isArray(scansData)) {
              if (typeof scansData === 'object' && scansData !== null) {
                scansData = [scansData];
              } else {
                return React.createElement(Typography, {
                  variant: 'body2',
                  color: 'error'
                }, 'Scan data is not in expected format');
              }
            }

            if (scansData.length === 0) {
              return React.createElement(Typography, {
                variant: 'body2',
                color: 'text.secondary'
              }, 'No scan history available');
            }

            // Get all unique keys from all scan objects
            const allKeys = [...new Set(scansData.flatMap(scan => Object.keys(scan)))];
            
            // Common field mappings for better display
            const fieldLabels = {
              date: 'Date',
              time: 'Time',
              datetime: 'Date & Time',
              timestamp: 'Timestamp',
              location: 'Location',
              city: 'City',
              status: 'Status',
              description: 'Description',
              action: 'Action',
              scanType: 'Scan Type',
              scan_type: 'Scan Type',
              remarks: 'Remarks',
              facility: 'Facility',
              facilityName: 'Facility Name',
              facility_name: 'Facility Name',
              statusDate: 'Status Date',
              statusTime: 'Status Time',
              statusType: 'Status Type',
              status_type: 'Status Type'
            };

            // Format field name for display
            const formatFieldName = (key) => {
              return fieldLabels[key.toLowerCase()] || key.split('_').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
            };

            return React.createElement(TableContainer, {
              component: Paper,
              sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                maxHeight: 400,
                overflow: 'auto'
              }
            },
              React.createElement(Table, {
                size: 'small',
                stickyHeader: true
              },
                React.createElement(TableHead, null,
                  React.createElement(TableRow, null,
                    allKeys.map(key => 
                      React.createElement(TableCell, {
                        key: key,
                        align: 'center',
                        sx: {
                          fontWeight: 600,
                          backgroundColor: '#f5f5f5',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          textAlign: 'center'
                        }
                      }, formatFieldName(key))
                    )
                  )
                ),
                React.createElement(TableBody, null,
                  scansData.map((scan, index) => 
                    React.createElement(TableRow, {
                      key: index,
                      sx: {
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(0, 0, 0, 0.01)'
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }
                      }
                    },
                      allKeys.map(key => 
                        React.createElement(TableCell, {
                          key: key,
                          align: 'center',
                          sx: {
                            borderColor: 'divider',
                            fontSize: '0.875rem',
                            textAlign: 'center'
                          }
                        }, 
                          scan[key] !== null && scan[key] !== undefined 
                            ? (typeof scan[key] === 'object' 
                                ? JSON.stringify(scan[key]) 
                                : String(scan[key]))
                            : '-'
                        )
                      )
                    )
                  )
                )
              )
            );
          })()
        )
      ),
      courierData.eventList && React.createElement(Accordion, {
        sx: { mt: 2 }
      },
        React.createElement(AccordionSummary, {
          expandIcon: React.createElement(ExpandMoreIcon, null)
        },
          React.createElement(Typography, null, 'Event List')
        ),
        React.createElement(AccordionDetails, null,
          (() => {
            // Parse eventList if it's a string, otherwise use as-is
            let eventListData = courierData.eventList;
            if (typeof eventListData === 'string') {
              try {
                eventListData = JSON.parse(eventListData);
              } catch (e) {
                return React.createElement(Typography, {
                  variant: 'body2',
                  color: 'error'
                }, 'Invalid event list data format');
              }
            }

            // Ensure it's an array
            if (!Array.isArray(eventListData)) {
              if (typeof eventListData === 'object' && eventListData !== null) {
                eventListData = [eventListData];
              } else {
                return React.createElement(Typography, {
                  variant: 'body2',
                  color: 'error'
                }, 'Event list data is not in expected format');
              }
            }

            if (eventListData.length === 0) {
              return React.createElement(Typography, {
                variant: 'body2',
                color: 'text.secondary'
              }, 'No events available');
            }

            // Get all unique keys from all event objects
            const allKeys = [...new Set(eventListData.flatMap(event => Object.keys(event)))];
            
            // Common field mappings for better display
            const fieldLabels = {
              docketNo: 'Docket No',
              docket_no: 'Docket No',
              event: 'Event',
              Event: 'Event',
              eventStatus: 'Event Status',
              event_status: 'Event Status',
              EventStatus: 'Event Status',
              eventDateTime: 'Date & Time',
              event_date_time: 'Date & Time',
              EventDateTime: 'Date & Time',
              eventLocation: 'Location',
              event_location: 'Location',
              EventLocation: 'Location',
              statusCode: 'Status Code',
              status_code: 'Status Code',
              StatusCode: 'Status Code',
              eventDescription: 'Description',
              event_description: 'Event Description',
              EventDescription: 'Description',
              date: 'Date',
              time: 'Time',
              datetime: 'Date & Time',
              timestamp: 'Timestamp',
              location: 'Location',
              city: 'City',
              status: 'Status',
              description: 'Description'
            };

            // Format field name for display
            const formatFieldName = (key) => {
              return fieldLabels[key] || key.split('_').map(word => 
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join(' ');
            };

            // Format cell value
            const formatCellValue = (value) => {
              if (value === null || value === undefined) return '-';
              if (typeof value === 'object') return JSON.stringify(value);
              // Format eventDateTime to readable format
              if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(value)) {
                try {
                  const dateObj = new Date(value);
                  if (!isNaN(dateObj.getTime())) {
                    const day = dateObj.getDate().toString().padStart(2, '0');
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const month = monthNames[dateObj.getMonth()];
                    const year = dateObj.getFullYear();
                    const hours24 = dateObj.getHours();
                    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
                    const period = hours24 >= 12 ? 'pm' : 'am';
                    const displayHours = hours24 % 12 || 12;
                    return `${day} ${month} ${year}, ${displayHours.toString().padStart(2, '0')}:${minutes} ${period}`;
                  }
                } catch {
                  // Fall through to return original value
                }
              }
              return String(value);
            };

            return React.createElement(TableContainer, {
              component: Paper,
              sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                maxHeight: 400,
                overflow: 'auto'
              }
            },
              React.createElement(Table, {
                size: 'small',
                stickyHeader: true
              },
                React.createElement(TableHead, null,
                  React.createElement(TableRow, null,
                    allKeys.map(key => 
                      React.createElement(TableCell, {
                        key: key,
                        align: 'center',
                        sx: {
                          fontWeight: 600,
                          backgroundColor: '#f5f5f5',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          textAlign: 'center'
                        }
                      }, formatFieldName(key))
                    )
                  )
                ),
                React.createElement(TableBody, null,
                  eventListData.map((event, index) => 
                    React.createElement(TableRow, {
                      key: index,
                      sx: {
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(0, 0, 0, 0.01)'
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }
                      }
                    },
                      allKeys.map(key => 
                        React.createElement(TableCell, {
                          key: key,
                          align: 'center',
                          sx: {
                            borderColor: 'divider',
                            fontSize: '0.875rem',
                            textAlign: 'center'
                          }
                        }, formatCellValue(event[key]))
                      )
                    )
                  )
                )
              )
            );
          })()
        )
      )
    )
  );
};

export default CourierTracking;

